import { useCallback } from 'react';

import { useIntl } from 'react-intl';

import { getMergeQueuePullRequestsUrl } from 'src/components/merge-queues/constants';
import { useFlag } from 'src/hooks/flag';
import { useMutation } from 'src/utils/fb-query';
import { authRequest, jsonHeaders } from 'src/utils/fetch';
import { captureExceptionWithTags } from 'src/utils/sentry';

import { messages } from '../merge-queues.i18n';
import { UsePullRequestActionParams } from '../types';

const dequeuePullRequestRequest = async ({
  mergeQueueUuid,
  pullRequestId,
  owner,
  slug,
}: Omit<UsePullRequestActionParams, 'onClose'>) => {
  const url = `${getMergeQueuePullRequestsUrl(
    owner,
    slug,
    mergeQueueUuid
  )}/${pullRequestId}`;

  const response = await fetch(
    authRequest(url, {
      method: 'DELETE',
      headers: jsonHeaders,
    })
  );

  if (!response.ok) {
    throw new Error(
      `Failed to dequeue pull request: ${response.status} ${response.statusText}`
    );
  }

  return Promise.resolve();
};

export const useDequeuePullRequest = ({
  mergeQueueUuid,
  owner,
  slug,
  onClose,
}: Omit<UsePullRequestActionParams, 'pullRequestId'>) => {
  const { showSuccessFlag, showErrorFlag } = useFlag();
  const { formatMessage } = useIntl();

  const dequeuePullRequestCallback = useCallback(
    (pullRequestId: number) =>
      dequeuePullRequestRequest({ mergeQueueUuid, pullRequestId, owner, slug }),
    [mergeQueueUuid, owner, slug]
  );

  const { mutate: dequeuePullRequest, isPending: isDequeueingPullRequest } =
    useMutation(dequeuePullRequestCallback, {
      onSuccess: () => {
        showSuccessFlag(formatMessage(messages.pullRequestDequeuedSuccess));
      },
      onError: (error: Error) => {
        captureExceptionWithTags(error, {
          section: 'merge-queue-pull-request',
          action: 'dequeue-pull-request',
        });
        showErrorFlag(formatMessage(messages.pullRequestDequeuedError));
      },
      onFinished: () => {
        onClose();
      },
    });

  return {
    dequeuePullRequest,
    isDequeueingPullRequest,
  };
};
